import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Calculator,
  Upload,
  Trash2,
  Loader2,
  FileSpreadsheet,
  Pencil,
  Check,
  X,
  AlertTriangle,
  Clock,
  HelpCircle,
  ExternalLink,
  FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { estimateApi, settingsApi } from '../lib/api';
import ManualEstimateBuilder from './ManualEstimateBuilder';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

// Craft code reference from Craftsman National Estimator
const CRAFT_CODES = {
  'B1': { title: '1 laborer & 1 carpenter', cost: 41.64 },
  'B2': { title: '1 laborer, 2 carpenters', cost: 43.05 },
  'B3': { title: '2 laborers, 1 carpenter', cost: 40.23 },
  'B4': { title: '1 laborer, 1 operating engineer, 1 reinforcing iron worker', cost: 45.25 },
  'B5': { title: '1 laborer, 1 carpenter, 1 cement mason, 1 operating engineer, 1 reinforcing iron worker', cost: 45.06 },
  'B6': { title: '1 laborer, 1 cement mason', cost: 40.53 },
  'B7': { title: '1 laborer, 1 truck driver', cost: 38.14 },
  'B8': { title: '1 laborer', cost: 44.25 },
  'B9': { title: '1 bricklayer, 1 bricklayer\'s helper', cost: 39.01 },
  'BB': { title: '1 bricklayer', cost: 44.21 },
  'BC': { title: '1 carpenter', cost: 45.88 },
  'BE': { title: '1 electrician', cost: 48.35 },
  'BF': { title: '1 floor layer', cost: 42.19 },
  'BG': { title: '1 glazier', cost: 43.85 },
  'BH': { title: '1 bricklayer\'s helper', cost: 33.80 },
  'BL': { title: '1 laborer', cost: 37.40 },
  'BR': { title: '1 lather', cost: 43.99 },
  'BS': { title: '1 marblesetter', cost: 37.59 },
  'CA': { title: '1 carpenter\'s assistant', cost: null },
  'CF': { title: '1 cement mason', cost: 43.66 },
  'CT': { title: '1 mosaic & terrazzo worker', cost: 39.86 },
  'D1': { title: '1 drywall installer, 1 drywall taper', cost: 44.42 },
  'DI': { title: '1 drywall installer', cost: 44.11 },
  'DT': { title: '1 drywall taper', cost: 44.72 },
  'HC': { title: '1 plasterer helper', cost: 34.39 },
  'OE': { title: '1 operating engineer', cost: 51.09 },
  'P1': { title: '1 laborer, 1 plumber', cost: 44.45 },
  'PM': { title: '1 plumber', cost: 51.49 },
  'PP': { title: '1 painter, 1 laborer', cost: 41.40 },
  'PR': { title: '1 plasterer', cost: 45.50 },
  'PT': { title: '1 painter', cost: 45.39 },
  'R1': { title: '1 roofer, 1 laborer', cost: 43.39 },
  'RI': { title: '1 reinforcing iron worker', cost: 47.27 },
  'RR': { title: '1 roofer', cost: 49.37 },
  'SW': { title: '1 sheet metal worker', cost: 49.64 },
  'T1': { title: '1 tile layer, 1 laborer', cost: 38.84 },
  'TL': { title: '1 tile layer', cost: 40.27 },
  'TR': { title: '1 truck driver', cost: 38.87 },
};

function parseCraftHours(craftStr) {
  if (!craftStr) return { code: null, hours: null };
  const match = craftStr.match(/^([A-Z0-9]+)\s*@\s*([\d.]+)/);
  if (match) return { code: match[1], hours: parseFloat(match[2]) };
  return { code: null, hours: null };
}

export default function EstimateSection({ leadId, projectId }) {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [openEstimate, setOpenEstimate] = useState(null);
  const [craftCodeOpen, setCraftCodeOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingManual, setEditingManual] = useState(null);
  const [estimateDefaults, setEstimateDefaults] = useState({
    labor_rate: 42, material_tax_pct: 6.85, cya_pct: 5, overhead_pct: 10,
  });
  const fileInputRef = useRef(null);

  const entityId = projectId || leadId;

  useEffect(() => {
    loadEstimates();
    checkEnabled();
    settingsApi.getEstimateDefaults()
      .then((res) => { if (res.data?.defaults) setEstimateDefaults(res.data.defaults); })
      .catch(() => {});
  }, [entityId]);

  const checkEnabled = async () => {
    try {
      const res = await settingsApi.get();
      const settings = res.data?.settings || {};
      setEnabled(settings.craftsman_estimates_enabled !== false);
    } catch { /* default enabled */ }
  };

  const loadEstimates = async () => {
    try {
      const res = projectId
        ? await estimateApi.getForProject(entityId)
        : await estimateApi.getForLead(entityId);
      setEstimates(res.data);
    } catch (err) {
      console.error('Failed to load estimates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.xlsx?$/i)) {
      toast.error('Please upload an Excel file (.xlsx)');
      return;
    }
    setUploading(true);
    try {
      const res = projectId
        ? await estimateApi.importForProject(entityId, file)
        : await estimateApi.importForLead(entityId, file);
      toast.success(`Estimate imported: ${res.data.line_items.length} line items`);
      if (res.data.parse_warnings?.length > 0) {
        toast.warning(`Parser warnings: ${res.data.parse_warnings.join(', ')}`);
      }
      loadEstimates();
    } catch (err) {
      toast.error(`Import failed: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (estimateId, filename) => {
    if (!window.confirm(`Delete estimate "${filename}"?`)) return;
    try {
      await estimateApi.delete(estimateId);
      setEstimates(estimates.filter(e => e.id !== estimateId));
      setOpenEstimate(null);
      toast.success('Estimate deleted');
    } catch {
      toast.error('Failed to delete estimate');
    }
  };

  const handleStatusChange = async (estimateId, status) => {
    try {
      await estimateApi.update(estimateId, { status });
      toast.success(`Estimate marked as ${status}`);
      loadEstimates();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleSell = async (estimate) => {
    const name = estimate.estimate_name || estimate.filename;
    const total = estimate.totals?.grand_total || 0;
    if (!window.confirm(`Sell "${name}" and create a project?\n\nContract value: $${total.toLocaleString()}\nThis will move the lead to Won.`)) return;
    try {
      const res = await estimateApi.sell(estimate.id);
      toast.success(`Project "${res.data.project_name}" created from estimate!`);
      loadEstimates();
      setOpenEstimate(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object') ? (detail.message || 'This lead already has a project.') : (detail || 'Unknown error');
      toast.error(`Failed: ${msg}`);
    }
  };

  const handleOpenEstimate = (estimate) => {
    if (estimate.source === 'manual') {
      setEditingManual(estimate);
      setBuilderOpen(true);
    } else {
      setOpenEstimate(estimate);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-sm" data-testid="estimate-section">
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-sm" data-testid="estimate-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            Estimates
            {estimates.length > 0 && (
              <Badge variant="secondary" className="ml-1">{estimates.length}</Badge>
            )}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditingManual(null); setBuilderOpen(true); }}
              className="rounded-sm"
              data-testid="build-estimate-btn"
            >
              <Calculator className="h-4 w-4 mr-2" /> Build Estimate
            </Button>
            {enabled && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImport}
                  className="hidden"
                  data-testid="estimate-file-input"
                />
                <Button
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="rounded-sm bg-orange-500 hover:bg-orange-600"
                  data-testid="import-estimate-btn"
                >
                  {uploading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" /> Import Craftsman</>
                  )}
                </Button>
              </>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {estimates.length === 0 ? (
          <p className="text-center text-muted-foreground py-6 text-sm">
            No estimates yet. Build one in-app or import an Excel file from Craftsman National Estimator.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {estimates.map(estimate => (
              <EstimateThumbnail
                key={estimate.id}
                estimate={estimate}
                onClick={() => handleOpenEstimate(estimate)}
                onStatusChange={(status) => handleStatusChange(estimate.id, status)}
                onSell={() => handleSell(estimate)}
                onInvoice={() => navigate(`/invoices/new?estimate_id=${estimate.id}`)}
              />
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground text-center pt-3">
          Internal cost data — not shown to clients unless you share a client PDF.
        </p>
      </CardContent>

      {/* Full-screen estimate dialog */}
      {openEstimate && (
        <EstimateFullDialog
          estimate={openEstimate}
          open={!!openEstimate}
          onOpenChange={(open) => { if (!open) setOpenEstimate(null); }}
          onDelete={() => handleDelete(openEstimate.id, openEstimate.filename)}
          onSell={() => handleSell(openEstimate)}
          onUpdate={async () => {
            await loadEstimates();
            // Refresh the open estimate with updated data
            try {
              const res = projectId
                ? await estimateApi.getForProject(entityId)
                : await estimateApi.getForLead(entityId);
              const updated = res.data.find(e => e.id === openEstimate.id);
              if (updated) setOpenEstimate(updated);
            } catch { /* dialog stays open with stale data, still fine */ }
          }}
          onCraftCodes={() => setCraftCodeOpen(true)}
        />
      )}

      <CraftCodeDialog open={craftCodeOpen} onOpenChange={setCraftCodeOpen} />

      {builderOpen && (
        <ManualEstimateBuilder
          open={builderOpen}
          onOpenChange={(o) => { setBuilderOpen(o); if (!o) setEditingManual(null); }}
          leadId={leadId}
          projectId={projectId}
          defaults={estimateDefaults}
          editing={editingManual}
          onSaved={loadEstimates}
        />
      )}
    </Card>
  );
}

/* Compact thumbnail card */
function EstimateThumbnail({ estimate, onClick, onStatusChange, onSell, onInvoice }) {
  const t = estimate.totals;
  const isSold = estimate.status === 'sold';

  const STATUS_COLORS = {
    draft: 'bg-slate-100 text-slate-700',
    imported: 'bg-slate-100 text-slate-700',
    active: 'bg-blue-100 text-blue-700',
    sold: 'bg-green-100 text-green-700',
    on_hold: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700',
    reviewed: 'bg-purple-100 text-purple-700',
    approved: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <div className="border rounded-sm p-3 hover:bg-muted/30 transition-colors" data-testid={`estimate-thumb-${estimate.id}`}>
      <div className="flex items-start justify-between gap-2">
        <button onClick={onClick} className="flex items-center gap-2 min-w-0 text-left group">
          <FileSpreadsheet className="h-8 w-8 text-orange-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate group-hover:underline">
              {estimate.estimate_name || estimate.filename}
            </p>
            <p className="text-xs text-muted-foreground">
              {estimate.line_items.length} items
              {t.manhours > 0 && <> &middot; {t.manhours} hrs</>}
            </p>
          </div>
        </button>
        <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0 mt-1 cursor-pointer" onClick={onClick} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-lg font-bold text-orange-600 tabular-nums">
          {formatCurrency(t.grand_total)}
        </span>
        <div className="flex items-center gap-1.5">
          {!isSold ? (
            <>
              <Select value={estimate.status} onValueChange={onStatusChange}>
                <SelectTrigger className={`h-6 text-[10px] w-24 rounded-sm px-2 border-0 ${STATUS_COLORS[estimate.status] || STATUS_COLORS.imported}`}
                  data-testid={`status-select-${estimate.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="imported">Imported</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="default"
                className="h-6 text-[10px] px-2 bg-green-600 hover:bg-green-700 rounded-sm"
                onClick={onSell}
                data-testid={`sell-btn-${estimate.id}`}>
                Sell
              </Button>
            </>
          ) : (
            <Badge className="text-[10px] bg-green-100 text-green-700">Sold</Badge>
          )}
          <Button size="sm" variant="outline"
            className="h-6 text-[10px] px-2 rounded-sm"
            onClick={onInvoice}
            data-testid={`invoice-btn-${estimate.id}`}>
            Invoice
          </Button>
        </div>
      </div>
    </div>
  );
}

/* Full-screen estimate dialog with editable table */
function EstimateFullDialog({ estimate, open, onOpenChange, onDelete, onSell, onUpdate, onCraftCodes }) {
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const t = estimate.totals;

  const startEdit = (item) => {
    setEditing(item.id);
    setEditValues({
      qty: item.qty,
      craft_hours: item.craft_hours || '',
      material: item.material,
      labor: item.labor,
      equipment: item.equipment,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValues({});
  };

  const handleCraftHoursChange = (newCraftStr) => {
    const updated = { ...editValues, craft_hours: newCraftStr };
    // Auto-calc labor if we can parse the craft code and have a rate
    const { code, hours } = parseCraftHours(newCraftStr);
    if (code && hours != null) {
      const codeInfo = CRAFT_CODES[code];
      if (codeInfo && codeInfo.cost) {
        updated.labor = Math.round(hours * codeInfo.cost * 100) / 100;
      }
    }
    setEditValues(updated);
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await estimateApi.getPdf(estimate.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const num = estimate.estimate_number || estimate.id.slice(0, 8);
      a.download = `Estimate-${num}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const saveEdit = async (itemId) => {
    const updatedItems = estimate.line_items.map(item => {
      if (item.id !== itemId) return item;
      const qty = parseFloat(editValues.qty) || 0;
      const material = parseFloat(editValues.material) || 0;
      const labor = parseFloat(editValues.labor) || 0;
      const equipment = parseFloat(editValues.equipment) || 0;
      return {
        ...item,
        qty,
        craft_hours: editValues.craft_hours || item.craft_hours,
        material,
        labor,
        equipment,
        total: material + labor + equipment,
      };
    });

    try {
      await estimateApi.update(estimate.id, { line_items: updatedItems });
      toast.success('Line item updated');
      setEditing(null);
      onUpdate();
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="sticky top-0 bg-background z-10 border-b px-6 py-4">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-3">
                <FileSpreadsheet className="h-5 w-5 text-orange-500" />
                {estimate.estimate_name || estimate.filename}
                {estimate.estimate_number && (
                  <span className="text-muted-foreground font-normal">#{estimate.estimate_number}</span>
                )}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={handleDownloadPdf} disabled={downloadingPdf}
                  className="text-xs" data-testid={`download-estimate-pdf-${estimate.id}`}>
                  {downloadingPdf ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1" />}
                  PDF
                </Button>
                <Button size="sm" variant="ghost" onClick={onCraftCodes} className="text-xs" data-testid="craft-codes-btn">
                  <HelpCircle className="h-3.5 w-3.5 mr-1" /> Craft Codes
                </Button>
                {estimate.status !== 'sold' && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs" onClick={onSell}
                    data-testid={`dialog-sell-btn-${estimate.id}`}>
                    Sell & Create Project
                  </Button>
                )}
                <Badge variant={estimate.status === 'sold' ? 'default' : 'secondary'}
                  className={estimate.status === 'sold' ? 'bg-green-100 text-green-700' : ''}>
                  {estimate.status}
                </Badge>
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={onDelete}
                  data-testid={`delete-estimate-${estimate.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Imported from Craftsman — {estimate.filename} — {new Date(estimate.created_at).toLocaleDateString()}
            </p>
          </DialogHeader>
        </div>

        {/* Parse warnings */}
        {estimate.parse_warnings?.length > 0 && (
          <div className="px-6 py-2 bg-amber-50 dark:bg-amber-950/20 border-b flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-700 dark:text-amber-400">
              {estimate.parse_warnings.map((w, i) => <p key={i}>{w}</p>)}
            </div>
          </div>
        )}

        {/* Line items table — FULL WIDTH */}
        <div className="overflow-x-auto px-2">
          <table className="w-full text-sm" data-testid={`estimate-table-${estimate.id}`}>
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-2.5 font-medium min-w-[200px]">Description</th>
                <th className="text-right px-3 py-2.5 font-medium w-16">Qty</th>
                <th className="text-left px-3 py-2.5 font-medium w-14">Unit</th>
                <th className="text-center px-3 py-2.5 font-medium w-32">Craft@Hours</th>
                <th className="text-right px-3 py-2.5 font-medium w-28">Material</th>
                <th className="text-right px-3 py-2.5 font-medium w-28">Labor</th>
                <th className="text-right px-3 py-2.5 font-medium w-24">Equip.</th>
                <th className="text-right px-3 py-2.5 font-medium w-28">Total</th>
                <th className="w-20 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {estimate.line_items.map((item) => {
                const { code, hours } = parseCraftHours(item.craft_hours);
                const codeInfo = code ? CRAFT_CODES[code] : null;

                return (
                  <tr key={item.id} className="border-b hover:bg-muted/20 group" data-testid={`estimate-line-${item.id}`}>
                    <td className="px-4 py-3">{item.description}</td>
                    {editing === item.id ? (
                      <>
                        <td className="px-2 py-1.5">
                          <Input type="number" value={editValues.qty}
                            onChange={e => setEditValues({...editValues, qty: e.target.value})}
                            className="h-8 text-right text-sm w-16" />
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{item.unit}</td>
                        <td className="px-2 py-1.5">
                          <Input type="text" value={editValues.craft_hours}
                            onChange={e => handleCraftHoursChange(e.target.value)}
                            placeholder="e.g. B1 @ 18.4"
                            className="h-8 text-center text-sm w-28" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input type="number" value={editValues.material}
                            onChange={e => setEditValues({...editValues, material: e.target.value})}
                            className="h-8 text-right text-sm w-24" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input type="number" value={editValues.labor}
                            onChange={e => setEditValues({...editValues, labor: e.target.value})}
                            className="h-8 text-right text-sm w-24" />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input type="number" value={editValues.equipment}
                            onChange={e => setEditValues({...editValues, equipment: e.target.value})}
                            className="h-8 text-right text-sm w-20" />
                        </td>
                        <td className="text-right px-3 py-2 font-medium">—</td>
                        <td className="px-2 py-1.5">
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" onClick={() => saveEdit(item.id)}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={cancelEdit}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="text-right px-3 py-3 tabular-nums">{item.qty}</td>
                        <td className="px-3 py-3 text-muted-foreground">{item.unit}</td>
                        <td className="px-3 py-3 text-center">
                          <CraftHoursBadge code={code} hours={hours} codeInfo={codeInfo} />
                        </td>
                        <td className="text-right px-3 py-3 tabular-nums">{formatCurrency(item.material)}</td>
                        <td className="text-right px-3 py-3 tabular-nums">{formatCurrency(item.labor)}</td>
                        <td className="text-right px-3 py-3 tabular-nums">{formatCurrency(item.equipment)}</td>
                        <td className="text-right px-3 py-3 font-semibold tabular-nums">{formatCurrency(item.total)}</td>
                        <td className="px-2">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100"
                            onClick={() => startEdit(item)} data-testid={`edit-line-${item.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="border-t bg-muted/30 px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 text-sm">
            {t.manhours > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Total Manhours</p>
                <p className="font-medium tabular-nums flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  {t.manhours} hrs
                </p>
              </div>
            )}
            {t.subtotal > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Subtotal</p>
                <p className="font-medium tabular-nums">{formatCurrency(t.subtotal)}</p>
              </div>
            )}
            {t.overhead > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Overhead {t.overhead_pct && `(${t.overhead_pct}%)`}</p>
                <p className="tabular-nums">{formatCurrency(t.overhead)}</p>
              </div>
            )}
            {t.contingency > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Contingency {t.contingency_pct && `(${t.contingency_pct}%)`}</p>
                <p className="tabular-nums">{formatCurrency(t.contingency)}</p>
              </div>
            )}
            {t.profit > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Profit {t.profit_pct && `(${t.profit_pct}%)`}</p>
                <p className="tabular-nums">{formatCurrency(t.profit)}</p>
              </div>
            )}
            {t.tax > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Tax {t.tax_pct && `(${t.tax_pct}%)`}</p>
                <p className="tabular-nums">{formatCurrency(t.tax)}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground">Grand Total</p>
              <p className="text-xl font-bold tabular-nums text-orange-600">{formatCurrency(t.grand_total)}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CraftCodeDialog({ open, onOpenChange }) {
  const codes = Object.entries(CRAFT_CODES).sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" /> Craft Codes, Hourly Costs & Crew Compositions
          </DialogTitle>
        </DialogHeader>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-1.5 px-2 font-medium w-16">Crew</th>
              <th className="text-right py-1.5 px-2 font-medium w-16">$/hr</th>
              <th className="text-left py-1.5 px-2 font-medium">Composition</th>
            </tr>
          </thead>
          <tbody>
            {codes.map(([code, info]) => (
              <tr key={code} className="border-b last:border-0 hover:bg-muted/30">
                <td className="py-1.5 px-2">
                  <Badge variant="outline" className="font-mono text-xs">{code}</Badge>
                </td>
                <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">
                  {info.cost ? `$${info.cost.toFixed(2)}` : '—'}
                </td>
                <td className="py-1.5 px-2">{info.title}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground pt-2 italic">
          Source: Craftsman National Estimator
        </p>
      </DialogContent>
    </Dialog>
  );
}

function CraftHoursBadge({ code, hours, codeInfo }) {
  if (!code && !hours) return <span className="text-xs text-muted-foreground">—</span>;
  const tooltip = codeInfo
    ? `${code}: ${codeInfo.title}${codeInfo.cost ? ` ($${codeInfo.cost.toFixed(2)}/hr)` : ''}`
    : code || '';
  return (
    <span className="inline-flex items-center gap-1.5 text-sm cursor-default" title={tooltip}>
      {code && <Badge variant="outline" className="font-mono text-xs px-1.5 py-0">{code}</Badge>}
      {hours != null && <span className="tabular-nums text-muted-foreground">{hours}h</span>}
    </span>
  );
}
